import { PlayerControlTypes } from '@omni/kit/components/KitPlayer/components/Controls/PlayerControlTypes/types';
import {
  CONTROLS_TIMEOUT,
  FAST_SEEK_INTERVAL_MS,
  FAST_SEEK_SPEED_MULT_ARR,
  PlayerControlsProps,
  PlayerControlsRef,
  SKIP_AMOUNT,
} from '@omni/kit/components/KitPlayer/components/Controls/types';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Linking, Platform } from 'react-native';

import Environment from '../../../../../Environment';
import { PlayFormat } from '../../../../../services/AnalyticsService/Types';
import { PlayerEvents } from '../../../playbackTypes';
import { sendPlayEvent } from '../../../utils/analytics';
import useCachedVolume from '../../PlayerVideo/hooks/useCachedVolume';
import useTrackActivity, { MOUSE_MOVE_DELAY } from './useTrackActivity';

export interface UsePlayerControlProps {
  actionIndicatorRef?: PlayerControlsProps['actionIndicatorRef'];
  analyticsFormat?: PlayFormat;
  appKey?: string;
  controls?: any;
  controlsContainerRef?: React.RefObject<any>;
  isLoading?: PlayerControlsProps['isLoading'];
  isMenuOrVolumeOpen?: boolean;
  isPlaying?: PlayerControlsProps['isPlaying'];
  isPrePlayPosterShowing?: PlayerControlsProps['isPrePlayPosterShowing'];
  isShareModalOpen?: boolean;
  mediaShortCode?: string;
  playerControlsRef: React.ForwardedRef<PlayerControlsRef>;
  playerTimelineRef?: PlayerControlsProps['playerTimelineRef'];
  playerVideoRef?: PlayerControlsProps['playerVideoRef'];
  playId?: string;
  previousTimestamp?: number;
  settingsControlRef?: PlayerControlsProps['settingsControlRef'];
  totalDurationSeconds?: PlayerControlsProps['totalDurationSeconds'];

  onFastSeekEnd?: PlayerControlsProps['onFastSeekEnd'];
  onFastSeekStart?: PlayerControlsProps['onFastSeekStart'];
  onPlaybackSpeedChange?: PlayerControlsProps['onPlaybackSpeedChange'];
  onSeekTo?: PlayerControlsProps['onSeekTo'];
  onSeekStart?: PlayerControlsProps['onSeekStart'];
  onToggleFullscreen?: PlayerControlsProps['onToggleFullscreen'];
  onTogglePlay?: PlayerControlsProps['onTogglePlay'];
  detectIsLiveStateOnSkip?: (skipAmountInSeconds: number) => void;
  setIsWatchingLive?: (isWatchingLive: boolean) => void;
}

interface UsePlayerControls {
  areControlsVisible: boolean;
  fastSeekDirection: 'forward' | 'backward';
  fastSeekMultiplierText: string;
  FocusedControl: React.MutableRefObject<PlayerControlTypes | undefined>;
  isFastSeeking: boolean;
  isMuted: boolean;
  isTimelineOnlyVisible: boolean;
  volume: number;
  handleContainerPress: () => void;
  handleGive: () => void;
  handlePlaybackSpeedChange: (speed: number) => void;
  handleMutechange: (shouldMute: boolean) => void;
  handleResume: () => void;
  handleSelectQualityLevel: (level: number, label: string) => void;
  handleStartOver: (progress?: number) => void;
  handleSelectSubtitleTrack: (trackIndex: number, language?: string) => void;
  handleToggleFullscreen: () => void;
  handleTogglePlay: () => void;
  handleVolumeChange: (volume: number) => void;
  hideControls: () => void;
  showControls: () => void;
  skipBackward: () => void;
  skipForward: () => void;
  sendAnalyticsPlayEvent: (event: PlayerEvents, data?: string) => void;
}

/**
 * Handles all shared player controls business logic.
 * This helps decouple business logic from the presentation layer.
 */
export default function usePlayerControls({
  actionIndicatorRef,
  analyticsFormat = 'video',
  appKey,
  controls = {},
  isLoading,
  isMenuOrVolumeOpen = false,
  isShareModalOpen = false,
  isPlaying,
  controlsContainerRef,
  mediaShortCode,
  playerControlsRef,
  playerTimelineRef,
  playerVideoRef,
  playId,
  previousTimestamp = 0,
  totalDurationSeconds,
  settingsControlRef,
  isPrePlayPosterShowing,

  onFastSeekEnd,
  onFastSeekStart,
  onPlaybackSpeedChange,
  onSeekTo,
  onToggleFullscreen,
  onTogglePlay,
  detectIsLiveStateOnSkip,
  setIsWatchingLive,
  onSeekStart,
}: UsePlayerControlProps): UsePlayerControls {
  const ControlVisibleTimerId =
    useRef<ReturnType<typeof setTimeout>>(undefined);
  const FastSeekDirectionMult = useRef(1);
  const FastSeekingIntervalId =
    useRef<ReturnType<typeof setTimeout>>(undefined);
  const FastSeekingMultiplier = useRef(1);
  const FastSeekingTime = useRef(-1);
  const FocusedControl = useRef<PlayerControlTypes>(undefined);
  const SkipCounter = useRef(0);
  const SkipTimeTracker = useRef(Date.now());

  const [areControlsVisible, setAreControlsVisible] = useState(true);
  const [fastSeekDirection, setFastSeekDirection] = useState<
    'forward' | 'backward'
  >('forward');
  const [fastSeekMultiplierText, setFastSeekMultiplierText] = useState('1x');
  const [isFastSeeking, setIsFastSeeking] = useState(false);
  const [isTimelineOnlyVisible, setIsTimelineOnlyVisible] = useState(false);
  const [isMuted, setIsMuted] = useState(false); /** Mute state button */
  const [volume, setVolume] = useState(1); /** Volume state button */

  const { isMouseMoving, isMouseClicking, isTouchDevice } = useTrackActivity({
    delay: MOUSE_MOVE_DELAY,
    containerRef: controlsContainerRef,
  });

  const sendAnalyticsPlayEvent = useCallback(
    (event: PlayerEvents, data?: string) => {
      if (!event || !playId || !appKey) return;

      sendPlayEvent({
        appKey,
        position: playerVideoRef?.current?.getCurrentTimeInSeconds(),
        event,
        format: analyticsFormat,
        playId,
        data,
      });
    },
    [analyticsFormat, appKey, playId, playerVideoRef]
  );

  const toggleControls = useCallback(
    ({ forceTo }: { forceTo?: boolean } = {}) => {
      setAreControlsVisible((areVisible) => {
        return forceTo !== undefined ? forceTo : !areVisible;
      });
    },
    []
  );

  const handleTogglePlay = useCallback(() => {
    onTogglePlay?.();

    if (isPlaying) {
      setIsWatchingLive?.(false);
    }
  }, [onTogglePlay, setIsWatchingLive, isPlaying]);

  const handleToggleFullscreen = useCallback(() => {
    onToggleFullscreen?.();
  }, [onToggleFullscreen]);

  const handleContainerPress = useCallback(() => {
    if (Platform.OS === 'web') {
      //in touch devices show controls on tap when media is playing
      if (isTouchDevice && !isPrePlayPosterShowing) {
        toggleControls();

        return;
      }

      handleTogglePlay?.();
    } else if (!Platform.isTV) {
      toggleControls();
    }
  }, [handleTogglePlay, toggleControls, isTouchDevice, isPrePlayPosterShowing]);

  const hideControls = useCallback(() => {
    if (ControlVisibleTimerId.current) {
      clearTimeout(ControlVisibleTimerId.current);
    }

    setAreControlsVisible(false);
    setIsTimelineOnlyVisible(false);
  }, []);

  const runTimeoutControls = useCallback(
    (customTimeout?: number) => {
      if (ControlVisibleTimerId.current) {
        clearTimeout(ControlVisibleTimerId.current);
      }

      ControlVisibleTimerId.current = setTimeout(() => {
        if (!isFastSeeking) {
          hideControls();
        }
      }, customTimeout ?? CONTROLS_TIMEOUT);
    },
    [hideControls, isFastSeeking]
  );

  const showControls = useCallback(() => {
    if (ControlVisibleTimerId.current) {
      clearTimeout(ControlVisibleTimerId.current);
    }

    setAreControlsVisible(true);
    runTimeoutControls();
  }, [runTimeoutControls]);

  const showTimelineIfFourSkips = useCallback(() => {
    SkipCounter.current++;
    if (Date.now() - SkipTimeTracker.current <= 2000) {
      if (SkipCounter.current >= 4) {
        SkipCounter.current = 0;
        SkipTimeTracker.current = Date.now();
        if (ControlVisibleTimerId.current) {
          clearTimeout(ControlVisibleTimerId.current);
        }

        runTimeoutControls(CONTROLS_TIMEOUT / 2);
        if (!areControlsVisible) {
          setIsTimelineOnlyVisible(true);
        }
      }
    } else {
      SkipCounter.current = 1;
      SkipTimeTracker.current = Date.now();
    }
  }, [areControlsVisible, runTimeoutControls]);

  const handleSeekTo = useCallback(
    (updater: number | ((t: number) => number)) => {
      onSeekStart?.();
      onSeekTo?.(updater);
    },
    [onSeekTo, onSeekStart]
  );

  const skipForward = useCallback(() => {
    if (isFastSeeking) return;

    sendAnalyticsPlayEvent(PlayerEvents.skipForward);

    detectIsLiveStateOnSkip?.(SKIP_AMOUNT);

    handleSeekTo?.((t) => t + SKIP_AMOUNT);

    if (!Platform.isTV) return;

    actionIndicatorRef?.current?.showAction('skip-forward');

    showTimelineIfFourSkips();
  }, [
    isFastSeeking,
    sendAnalyticsPlayEvent,
    handleSeekTo,
    actionIndicatorRef,
    showTimelineIfFourSkips,
    detectIsLiveStateOnSkip,
  ]);

  const skipBackward = useCallback(() => {
    if (isFastSeeking) return;

    sendAnalyticsPlayEvent(PlayerEvents.skipBackward);

    detectIsLiveStateOnSkip?.(-SKIP_AMOUNT);

    handleSeekTo?.((t) => t - SKIP_AMOUNT);

    if (!Platform.isTV) return;

    actionIndicatorRef?.current?.showAction('skip-backward');

    showTimelineIfFourSkips();
  }, [
    isFastSeeking,
    sendAnalyticsPlayEvent,
    handleSeekTo,
    actionIndicatorRef,
    showTimelineIfFourSkips,
    detectIsLiveStateOnSkip,
  ]);

  const stopFastSeeking = useCallback(() => {
    if (FastSeekingIntervalId.current) {
      clearInterval(FastSeekingIntervalId.current);
    }

    setIsFastSeeking(false);

    handleSeekTo?.(FastSeekingTime.current);
    onFastSeekEnd?.();
    FastSeekingTime.current = -1;
  }, [onFastSeekEnd, handleSeekTo]);

  const incrementFastSeeking = useCallback(() => {
    if (FastSeekingMultiplier.current >= FAST_SEEK_SPEED_MULT_ARR.length) {
      return;
    }

    FastSeekingMultiplier.current++;
    setFastSeekMultiplierText(`${FastSeekingMultiplier.current}x`);
  }, []);

  const handleFastSeekInterval = useCallback(() => {
    const fastSeekMult =
      FAST_SEEK_SPEED_MULT_ARR[FastSeekingMultiplier.current - 1];
    const stepPerIntvl = fastSeekMult / (1000 / FAST_SEEK_INTERVAL_MS);
    FastSeekingTime.current += stepPerIntvl * FastSeekDirectionMult.current;

    if (
      totalDurationSeconds &&
      FastSeekingTime.current >= totalDurationSeconds
    ) {
      return stopFastSeeking();
    } else if (FastSeekingTime.current <= 0) {
      return stopFastSeeking();
    }

    playerTimelineRef?.current?.fastSeekUpdate(FastSeekingTime.current);
  }, [playerTimelineRef, stopFastSeeking, totalDurationSeconds]);

  const startFastSeeking = useCallback(
    (direction: 'forward' | 'backward') => {
      if (!totalDurationSeconds) {
        return;
      }

      FastSeekingTime.current =
        playerVideoRef?.current?.getCurrentTimeInSeconds() ?? -1;
      playerTimelineRef?.current?.fastSeekUpdate(FastSeekingTime.current);

      onFastSeekStart?.();
      setIsFastSeeking(true);
      setFastSeekDirection(direction);
      setAreControlsVisible(false);
      setIsTimelineOnlyVisible(true);

      setFastSeekMultiplierText('1x');
      FastSeekingMultiplier.current = 1;

      const directionMultiplier = direction === 'forward' ? 1 : -1;
      FastSeekDirectionMult.current = directionMultiplier;

      if (FastSeekingIntervalId.current) {
        clearInterval(FastSeekingIntervalId.current);
      }

      FastSeekingIntervalId.current = setInterval(
        handleFastSeekInterval,
        FAST_SEEK_INTERVAL_MS
      );
    },
    [
      handleFastSeekInterval,
      onFastSeekStart,
      playerTimelineRef,
      playerVideoRef,
      totalDurationSeconds,
    ]
  );

  const handlePlaybackSpeedChange = useCallback(
    (speed: number) => {
      onPlaybackSpeedChange?.(speed);
      sendAnalyticsPlayEvent(PlayerEvents.playbackSpeedUpdate, `${speed}x`);
    },
    [onPlaybackSpeedChange, sendAnalyticsPlayEvent]
  );

  const handleMutechange = useCallback(
    (shouldMute: boolean) => {
      if (shouldMute === isMuted) return;

      setIsMuted(shouldMute);
      playerVideoRef?.current?.setMute(shouldMute);
      sendAnalyticsPlayEvent(
        shouldMute ? PlayerEvents.mute : PlayerEvents.unmute
      );
    },
    [playerVideoRef, sendAnalyticsPlayEvent, isMuted]
  );

  const handleSelectQualityLevel = useCallback(
    (level: number, label: string) => {
      playerVideoRef?.current?.selectQualityLevel(level);
      sendAnalyticsPlayEvent(PlayerEvents.adaptiveBitrateChange, label);
    },
    [playerVideoRef, sendAnalyticsPlayEvent]
  );

  const handleSelectSubtitleTrack = useCallback(
    (trackIndex: number, language?: string) => {
      playerVideoRef?.current?.selectSubtitleTrack(trackIndex);
      sendAnalyticsPlayEvent(
        trackIndex === -1
          ? PlayerEvents.subtitlesDisabled
          : PlayerEvents.subtitlesEnabled,
        trackIndex === -1 ? 'off' : language
      );
    },
    [playerVideoRef, sendAnalyticsPlayEvent]
  );

  const handleStartOver = useCallback(() => {
    sendAnalyticsPlayEvent(PlayerEvents.startOver);

    handleSeekTo?.(0);

    handleTogglePlay?.();
  }, [handleSeekTo, handleTogglePlay, sendAnalyticsPlayEvent]);

  const handleResume = useCallback(() => {
    sendAnalyticsPlayEvent(PlayerEvents.resumePlay);

    handleSeekTo?.(previousTimestamp);

    handleTogglePlay?.();
  }, [
    handleSeekTo,
    handleTogglePlay,
    previousTimestamp,
    sendAnalyticsPlayEvent,
  ]);

  const handleVolumeChange = useCallback(
    (volume: number) => {
      if (volume < 0) {
        volume = 0;
      } else if (volume > 1) {
        volume = 1;
      }

      setVolume(volume);
      playerVideoRef?.current?.setVolume(volume);
    },
    [playerVideoRef]
  );

  const handleGive = useCallback(() => {
    if (!appKey || !mediaShortCode) {
      return;
    }

    const givingUrl = `${Environment.newGivingHost}/u/-${appKey}/give?utm_campaign=media_giving&utm_source=web-embed&utm_medium=page&utm_content=${appKey}_${mediaShortCode}`;
    Linking.openURL(givingUrl);

    sendAnalyticsPlayEvent(PlayerEvents.give);
  }, [appKey, mediaShortCode, sendAnalyticsPlayEvent]);

  useCachedVolume({
    onUpdateMute: handleMutechange,
    onUpdateVolume: handleVolumeChange,
    volumeEnabled: controls.volume,
  });

  useImperativeHandle(
    playerControlsRef,
    () => ({
      toggleControls,
      skipForward,
      skipBackward,
      fastSeekForward() {
        if (isFastSeeking) return;

        startFastSeeking('forward');
      },
      fastSeekBackward() {
        if (isFastSeeking) return;

        startFastSeeking('backward');
      },
      stopFastSeeking,
      incrementFastSeeking,
      getAreControlsVisible() {
        return areControlsVisible;
      },
      getIsFastSeeking() {
        return isFastSeeking;
      },
      getFastSeekingDirection() {
        return fastSeekDirection;
      },
      getFocusedControl() {
        return FocusedControl.current;
      },
      getSettingsControlRef() {
        return settingsControlRef?.current;
      },
    }),
    [
      toggleControls,
      skipForward,
      skipBackward,
      stopFastSeeking,
      incrementFastSeeking,
      isFastSeeking,
      startFastSeeking,
      areControlsVisible,
      fastSeekDirection,
      settingsControlRef,
    ]
  );

  /** Show controls when pausing and is loading */
  useEffect(() => {
    if (!isPlaying) {
      setAreControlsVisible(true);
    }

    if (isLoading) {
      setAreControlsVisible(true);
    }
  }, [isLoading, isPlaying, setAreControlsVisible]);

  /** Timeout controls when playing or not loading */
  useEffect(() => {
    if (ControlVisibleTimerId.current) {
      clearTimeout(ControlVisibleTimerId.current);
    }

    if (Platform.OS === 'web') {
      if (
        isPlaying &&
        !isMouseMoving &&
        !isMouseClicking &&
        !isMenuOrVolumeOpen &&
        !isShareModalOpen &&
        !isLoading &&
        (areControlsVisible || isTimelineOnlyVisible)
      ) {
        runTimeoutControls();
      }
    } else {
      if (isPlaying && (areControlsVisible || isTimelineOnlyVisible)) {
        runTimeoutControls();
      }
    }

    return () => {
      if (ControlVisibleTimerId.current) {
        clearTimeout(ControlVisibleTimerId.current);
      }
    };
  }, [
    isPlaying,
    isLoading,
    isMenuOrVolumeOpen,
    isShareModalOpen,
    areControlsVisible,
    runTimeoutControls,
    isTimelineOnlyVisible,
    ControlVisibleTimerId,
    isMouseMoving,
    isMouseClicking,
  ]);

  return {
    areControlsVisible,
    fastSeekDirection,
    fastSeekMultiplierText,
    FocusedControl,
    isFastSeeking,
    isMuted,
    isTimelineOnlyVisible,
    volume,

    sendAnalyticsPlayEvent,
    handleContainerPress,
    handleGive,
    handleMutechange,
    handlePlaybackSpeedChange,
    handleResume,
    handleSelectQualityLevel,
    handleSelectSubtitleTrack,
    handleStartOver,
    handleToggleFullscreen,
    handleTogglePlay,
    handleVolumeChange,
    hideControls,
    showControls,
    skipBackward,
    skipForward,
  };
}
